import {
    Accordion,
    ApplicationType,
    Module,
    moduleNames,
    modules,
    RoleType,
    useOutsideClick,
} from "modules/common";
import { createModuleUsageLogData } from "modules/dashboard/store/moduleActivity/thunks";
import React, { RefObject, useCallback, useContext, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";
import { AppRoutes } from "routing";
import { CreateModuleUsageLogRequest } from "services/logger";
import { RootState } from "store";
import { AccessibilityContext } from "views/AccessibilityContext";

const HamburgerItem: React.FC<{ module: Module; onClick: () => void }> = ({ module, onClick }) => {
    const dispatch = useDispatch();

    const handleCreateModuleUsageLog = () => {
        const moduleValue = Object.values(moduleNames).find(
            moduleName => moduleName.label === module.label
        )?.value;

        if (!moduleValue) return;

        const createModuleUsageLogRequest: CreateModuleUsageLogRequest = {
            moduleUsageType: moduleValue,
            applicationSourceType: ApplicationType.Web,
        };
        dispatch(createModuleUsageLogData(createModuleUsageLogRequest));
    };

    const handleCombinedClick = () => {
        handleCreateModuleUsageLog();
        onClick();
    };

    const content = (
        <>
            <span>{module.label}</span>
        </>
    );

    return module.route ? (
        <NavLink
            to={module.route}
            className="hamburger-menu__item"
            activeClassName="hamurger-menu__item--active"
            onClick={handleCombinedClick}
        >
            {content}
        </NavLink>
    ) : (
        <Accordion
            title={module.label}
            className="hamburger-menu__accordion"
            isOpenByDefault={false}
            headerClassName="hamburger-menu__item"
        >
            <ul className="hamburger-menu__accordion-list">
                {module.children?.map(childModule =>
                    childModule.route ? (
                        <NavLink
                            key={childModule.id}
                            to={childModule.route}
                            className="hamburger-menu__accordion-item"
                            activeClassName="hamburger-menu__accordion-item--active"
                            onClick={handleCombinedClick}
                        >
                            <li className="hamburger-menu__accordion-item-content">
                                {childModule.label}
                            </li>
                        </NavLink>
                    ) : childModule.externalUrl ? (
                        <a
                            key={childModule.id}
                            href={childModule.externalUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hamburger-menu__accordion-item"
                            onClick={handleCombinedClick}
                        >
                            <li className="hamburger-menu__accordion-item-content">
                                {childModule.label}
                            </li>
                        </a>
                    ) : null
                )}
            </ul>
        </Accordion>
    );
};

interface Props {
    close: () => void;
    className?: string;
    isOpen: boolean;
    toggleButtonRef: RefObject<HTMLDivElement>;
}

export const HamburgerMenu: React.FC<Props> = ({ close, className, isOpen, toggleButtonRef }) => {
    const hamburgerMenuRef = useRef<HTMLDivElement>(null);
    const { profile } = useSelector((state: RootState) => state.profile);
    const isUserLoggedIn = useSelector((state: RootState) => state.signIn?.signInSuccess);
    const roleType = profile?.roleType?.value ?? RoleType.None;

    const isModuleAccessible = useCallback(
        (module: Module) =>
            (!Boolean(roleType) && module.roles.includes(RoleType.None)) ||
            (module.roles.includes(roleType) && profile?.moduleName.includes(module.id)),
        [profile?.moduleName, roleType]
    );

    const handleOutsideClick = () => close();
    useOutsideClick(hamburgerMenuRef, handleOutsideClick, toggleButtonRef);
    const {lowVision} = useContext(AccessibilityContext);

    if (!isOpen) return null;

    return (
        <nav
            ref={hamburgerMenuRef}
            role="navigation"
            className={`hamburger-menu ${className ?? ""}`}
        >
            {modules(lowVision ?? false).map(module => {
                if (!isModuleAccessible(module)) return null;
                
                if (module.children) {
                    const accessibleChildren = module.children.filter(isModuleAccessible);
                    if (accessibleChildren.length === 0) return null;
                    
                    const moduleWithFilteredChildren = {
                        ...module,
                        children: accessibleChildren
                    };
                    return <HamburgerItem key={module.id} module={moduleWithFilteredChildren} onClick={close} />;
                }
                
                return <HamburgerItem key={module.id} module={module} onClick={close} />;
            })}

            {isUserLoggedIn ? (
                <HamburgerItem
                    module={{
                        id: -1,
                        label: "Korisnički profil",
                        route: AppRoutes.Profile,
                        roles: [],
                    }}
                    onClick={close}
                />
            ) : null}
        </nav>
    );
};
